'use client';
import groq from 'groq';
import { useSearchParams } from 'next/navigation';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  CoachVisibility,
  PackageStatus,
  PackageVisibility,
  type Scalars,
  SchoolAdmissionStatus,
} from '../../__generated-gql-types__/globalTypes';
import CoachArticles from '../../components/coach/CoachArticles';
import CoachBio from '../../components/coach/CoachBio';
import CoachBottomMessageCTA from '../../components/coach/CoachBottomMessageCTA';
import { CoachCategories } from '../../components/coach/CoachCategories';
import CoachEducation from '../../components/coach/CoachEducation';
import CoachExperience from '../../components/coach/CoachExperience';
import CoachGroupClasses from '../../components/coach/CoachGroupClasses';
import CoachInfo from '../../components/coach/CoachInfo';
import CoachMessageCTA from '../../components/coach/CoachMessageCTA';
import CoachOfferings from '../../components/coach/CoachOfferings';
import CoachQualifications from '../../components/coach/CoachQualifications';
import CoachReviews from '../../components/coach/CoachReviews';
import CoachWhyICoach from '../../components/coach/CoachWhyICoach';
import { useBookHourlyCoachingModal } from '../../components/coach/modals/BookHourlyCoachingModal';
import NotFoundPage from '../../components/page/NotFoundPage';
import { useLeaveAReviewModal } from '../../components/review/modals/LeaveAReviewModal';
import { useOutcomeModal } from '../../components/review/modals/OutcomeModal';
import {
  ARTICLE_SELECTION_AND_ORDERING,
  BASE_ARTICLES_FILTER,
} from '../../lib/sanity/commonGroqs';
import { sanityClientWithCdn } from '../../lib/sanity/sanityClient';
import mxpnl from '../../utils/analytics/mixpanel';
import phog from '../../utils/analytics/posthog/posthog';
import { useOnce } from '../../utils/hooks/useOnce';
import { useReferralParameters } from '../../utils/hooks/useReferralParameters';
import useVisibility from '../../utils/hooks/useVisibility';
import { DEFAULT_HOURLY_ORDER_MINUTES } from '../../utils/order';
import { DEFAULT_REVIEW_FORM_VALUES } from '../../utils/review';
import {
  LelandSessionStorage,
  REFERRAL_CODE_KEY,
  REFERRAL_TYPE_KEY,
} from '../../utils/storage';
import { useLoginModal } from '../LoginModal';
import { OrganizationType } from '../common/OrganizationImg';
import OrganizationTag from '../common/OrganizationTag';

import {
  type CoachPageCoachFragment,
  type CoachPageUserFragment,
  useCoachPageGetHourlyOrderQuery,
  useCoachPageGetUserQuery,
} from './__generated-gql-types__/CoachPage.generated';
import { useAllCoachReviewsModal } from './modals/AllCoachReviewsModal';
export interface CoachPageProps {
  user: Possible<CoachPageUserFragment>;
  coach: Possible<CoachPageCoachFragment>;
  selectedCategoryUrn: Nullable<Scalars['CategoryUrn']['output']>;
}

const CoachPage: React.FC<CoachPageProps> = ({
  user,
  coach,
  selectedCategoryUrn,
}) => {
  const { setOpen: setLoginModalOpen, Modal: LoginModal } = useLoginModal();
  const isLoggedIn = !!user?.applicant;
  const searchParams = useSearchParams();
  const { referralCode, referralType } = useReferralParameters();
  const { data: clientUserData } = useCoachPageGetUserQuery();

  const currentUser = clientUserData?.user ?? user;

  const categoryToUse = coach?.categoryInformationList?.find(
    ({ category }) => category.id === selectedCategoryUrn,
  );

  useOnce(
    () => {
      if (referralCode && referralType && !isLoggedIn) {
        LelandSessionStorage.setItem(REFERRAL_CODE_KEY, referralCode);
        LelandSessionStorage.setItem(REFERRAL_TYPE_KEY, referralType);
        setLoginModalOpen(true);
      }
    },
    !!(referralCode && referralType && !isLoggedIn),
  );

  const [articles, setArticles] = useState<CMSArticle[]>();
  useEffect(() => {
    if (coach?.slug) {
      void (async () =>
        setArticles(
          await sanityClientWithCdn.fetch<CMSArticle[]>(
            groq`*[${BASE_ARTICLES_FILTER} && coach->slug.current == $slug]${ARTICLE_SELECTION_AND_ORDERING}`,
            { slug: coach.slug },
          ),
        ))();
    }
  }, [coach?.slug]);

  const [hasReviewed, setHasReviewed] = useState(false);
  const { setOpen: setIsReviewModalOpen, Modal: LeaveAReviewModal } =
    useLeaveAReviewModal();

  const { setOpen: setIsOutcomeModalOpen, Modal: OutcomeModal } =
    useOutcomeModal();

  const onReviewSuccess = () => {
    setHasReviewed(true);
    setIsReviewModalOpen(false);
    setIsOutcomeModalOpen(true);
  };

  const {
    setOpen: setIsBookHourlyCoachingModalOpen,
    Modal: BookHourlyCoachingModal,
  } = useBookHourlyCoachingModal();

  const hourlyOrderUrn = searchParams.get('order') as
    | Scalars['OrderUrn']['output']
    | undefined;

  const { data: hourlyOrderData, loading: hourlyOrderLoading } =
    useCoachPageGetHourlyOrderQuery({
      variables: {
        id: hourlyOrderUrn ?? '',
      },
      skip: !hourlyOrderUrn,
    });

  const messageCTARef = useRef<HTMLDivElement>(null);
  const isMessageCTAVisible = useVisibility(messageCTARef, true);

  const { setOpen: setIsAllReviewsModalOpen, Modal: AllCoachReviewsModal } =
    useAllCoachReviewsModal();

  useEffect(() => {
    if (hourlyOrderUrn) {
      setIsBookHourlyCoachingModalOpen(true);
    }
  }, [hourlyOrderUrn, setIsBookHourlyCoachingModalOpen]);

  const categoryInformationList = coach?.categoryInformationList;

  useEffect(() => {
    if (coach) {
      mxpnl.track('viewedCoachProfile', {
        coachId: coach.id,
        coachSlug: coach.slug,
        category: categoryInformationList?.find(
          (c) => c.category.id === selectedCategoryUrn,
        )?.category.slug,
      });
      phog.capture('coach_profile--view', {
        coachId: coach.id,
        coachSlug: coach.slug,
        category: categoryInformationList?.find(
          (c) => c.category.id === selectedCategoryUrn,
        )?.category.slug,
      });
    }
  }, [coach, categoryInformationList, selectedCategoryUrn]);

  const livePackages = useMemo(() => {
    return (
      coach?.packages?.filter(
        (p) =>
          p.status === PackageStatus.LIVE &&
          p.lelandPackageVisibility === PackageVisibility.PUBLIC,
      ) ?? []
    );
  }, [coach?.packages]);

  const groupClasses = coach?.groupClassesAndShindigs ?? [];

  const services = categoryInformationList?.find(
    (c) => c.category.id === selectedCategoryUrn,
  )?.coachingFocusList;

  const mostRecentOrder = coach?.mostRecentOrder;
  const mostRecentHourlyOrder = mostRecentOrder?.hourlyOrder ?? null;

  const coachProfessionalExperiences = coach?.professionalExperiences ?? [];
  const coachEducationExperiences = coach?.educationExperiences ?? [];

  const schoolsPersonallyAdmittedTo = (
    coach?.schoolAdmissionInfoList ?? []
  ).filter(
    (schoolAdmissionInfo) =>
      schoolAdmissionInfo?.status === SchoolAdmissionStatus.ADMITTED,
  );

  const companiesAdmittedTo = coach?.companiesAdmitted ?? [];

  const isCoachTakingNewClients =
    coach?.visibility !== CoachVisibility.PRIVATE_CLOSED ||
    !!coach.coachingRelationship;

  const categoryUrnForReview =
    // first check if selectedCategoryUrn is in relationship
    (
      coach?.coachingRelationship?.categories?.find(
        (c) => c.id === selectedCategoryUrn,
        // fallback to first category in relationship
      ) ?? coach?.coachingRelationship?.categories?.[0]
    )?.id;

  useEffect(() => {
    if (
      coach?.id &&
      searchParams.get('reviewFor') === coach.id &&
      categoryUrnForReview
    ) {
      setIsReviewModalOpen(true);
    }
  }, [
    coach?.id,
    searchParams.get('reviewFor'),
    setIsReviewModalOpen,
    categoryUrnForReview,
  ]);

  const handleBuyCoaching = () => setIsBookHourlyCoachingModalOpen(true);

  if (coach == null) {
    return <NotFoundPage entity="coach" />;
  }

  const isUserBlocked = !!coach.coachingRelationship?.blockedByCoach;

  return (
    <>
      <article className="mx-auto my-8 w-full max-w-page px-4 lg:px-6">
        <div className="grid grid-cols-12 md:gap-14">
          <div className="col-span-12 md:col-span-8">
            <div
              ref={messageCTARef}
              className="flex flex-col items-center justify-center pb-8 md:items-start md:pb-10"
            >
              <CoachInfo
                coach={coach}
                categoryId={selectedCategoryUrn}
                onReviewsClick={() => setIsAllReviewsModalOpen(true)}
              />

              {/* MOBILE */}
              <CoachMessageCTA
                user={user}
                className="mt-1 block w-full md:hidden"
                goal={categoryToUse?.category.goal}
                coach={coach}
                onReviewsClick={() => setIsAllReviewsModalOpen(true)}
                onBuyCoaching={handleBuyCoaching}
                categoryUrn={selectedCategoryUrn}
                setIsReviewModalOpen={setIsReviewModalOpen}
                blocked={isUserBlocked}
              />
              {categoryUrnForReview ? (
                <LeaveAReviewModal
                  coach={coach}
                  defaultValues={{
                    ...DEFAULT_REVIEW_FORM_VALUES,
                    categoryReviews: [{ categoryUrn: categoryUrnForReview }],
                  }}
                  onSuccess={onReviewSuccess}
                />
              ) : null}
              {currentUser?.applicant?.id &&
              categoryInformationList &&
              selectedCategoryUrn ? (
                <OutcomeModal
                  categoryInfo={categoryInformationList}
                  coachName={coach.user.firstName}
                  defaultValues={{
                    applicantUrn: currentUser.applicant.id,
                    categoryUrn: selectedCategoryUrn,
                  }}
                />
              ) : null}
            </div>

            {!selectedCategoryUrn && categoryInformationList?.length ? (
              <section className="border-t border-leland-gray-stroke py-8 md:py-10">
                <CoachCategories
                  coachSlug={coach.slug}
                  coachFirstName={coach.user.firstName}
                  categoryInformationList={categoryInformationList}
                />
              </section>
            ) : null}

            {selectedCategoryUrn && isCoachTakingNewClients ? (
              <section className="border-t border-leland-gray-stroke py-8 md:py-10">
                <CoachOfferings
                  livePackages={livePackages}
                  coach={coach}
                  categoryUrn={selectedCategoryUrn}
                  services={services}
                  onGetStarted={handleBuyCoaching}
                  headline={`${coach.user.firstName}'s Coaching Offerings`}
                  largeHeadline
                  blocked={isUserBlocked}
                />
              </section>
            ) : null}

            {groupClasses.length > 0 ? (
              <section className="border-t border-leland-gray-stroke py-8 md:py-10">
                <CoachGroupClasses
                  coachFirstName={coach.user.firstName}
                  groupClasses={groupClasses}
                />
              </section>
            ) : null}

            {selectedCategoryUrn ? (
              <section className="border-t border-leland-gray-stroke py-8 md:py-10">
                <CoachQualifications
                  coach={coach}
                  categoryId={selectedCategoryUrn}
                  viewAllReviews={() => setIsAllReviewsModalOpen(true)}
                />
              </section>
            ) : null}

            {coach.bio ? (
              <section className="border-t border-leland-gray-stroke py-8 md:py-10">
                <CoachBio className="mx-auto" coach={coach} />
              </section>
            ) : null}

            {coach.whyICoach ? (
              <section className="border-t border-leland-gray-stroke py-8 md:py-10">
                <CoachWhyICoach coach={coach} />
              </section>
            ) : null}

            {coachProfessionalExperiences.length > 0 ? (
              <section className="border-t border-leland-gray-stroke py-8 md:py-10">
                <CoachExperience
                  professionalExperiences={coachProfessionalExperiences}
                />
                {companiesAdmittedTo.length > 0 ? (
                  <>
                    <p className="mb-3 mt-10 text-base font-normal text-leland-gray-light md:mt-14">
                      {coach.user.firstName} was also given offers to work at
                    </p>
                    <ul className="flex flex-wrap items-center justify-start space-x-2">
                      {companiesAdmittedTo.map((company) => (
                        <li
                          key={company.id}
                          className="mb-2 flex items-center justify-start"
                        >
                          <OrganizationTag
                            tagDetails={{
                              organization: {
                                name: company.name,
                                logoLink: company.logoLink,
                              },
                            }}
                            type={OrganizationType.COMPANY}
                          />
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </section>
            ) : null}

            {coachEducationExperiences.length > 0 ? (
              <section className="border-t border-leland-gray-stroke py-8 md:py-10">
                <CoachEducation
                  educationExperiences={coachEducationExperiences}
                />
                {schoolsPersonallyAdmittedTo.length > 0 ? (
                  <>
                    <p className="mb-3 mt-10 text-base font-normal text-leland-gray-light md:mt-14">
                      {coach.user.firstName} was also personally admitted to
                    </p>
                    <ul className="flex flex-wrap items-center justify-start space-x-2">
                      {schoolsPersonallyAdmittedTo.map(
                        (schoolAdmissionInfo) => (
                          <li
                            key={schoolAdmissionInfo.school.id}
                            className="mb-2 flex items-center justify-start"
                          >
                            <OrganizationTag
                              tagDetails={{
                                organization: {
                                  name: schoolAdmissionInfo.school.name,
                                  logoLink: schoolAdmissionInfo.school.logoLink,
                                },
                              }}
                              type={OrganizationType.SCHOOL}
                            />
                          </li>
                        ),
                      )}
                    </ul>
                  </>
                ) : null}
              </section>
            ) : null}
          </div>

          <CoachMessageCTA
            user={user}
            className="hidden md:col-span-4 md:block"
            coach={coach}
            goal={categoryToUse?.category.goal}
            onReviewsClick={() => setIsAllReviewsModalOpen(true)}
            onBuyCoaching={handleBuyCoaching}
            categoryUrn={selectedCategoryUrn}
            setIsReviewModalOpen={setIsReviewModalOpen}
            blocked={isUserBlocked}
          />
        </div>

        {coach.reviewTotal > 0 ? (
          <section className="col-span-12 border-t border-leland-gray-stroke py-8 md:py-10">
            <CoachReviews
              coach={coach}
              categoryUrn={selectedCategoryUrn}
              onViewAllReviews={() => setIsAllReviewsModalOpen(true)}
              hasReviewed={hasReviewed}
              setReviewModalOpen={(open: boolean) => {
                setIsAllReviewsModalOpen(false);
                setIsReviewModalOpen(open);
              }}
            />
          </section>
        ) : null}

        {(articles?.length ?? 0) > 0 ? (
          <section className="col-span-12 border-t border-leland-gray-stroke py-8 md:py-10">
            <CoachArticles articles={articles} />
          </section>
        ) : null}
      </article>

      {/* MOBILE */}
      <CoachBottomMessageCTA
        user={user}
        goal={categoryToUse?.category.goal}
        isVisible={!isMessageCTAVisible}
        coach={coach}
        viewAllReviews={() => setIsAllReviewsModalOpen(true)}
        categoryUrn={selectedCategoryUrn}
        blocked={isUserBlocked}
      />

      <AllCoachReviewsModal
        coach={coach}
        hasReviewed={hasReviewed}
        setReviewModalOpen={(open: boolean) => {
          setIsAllReviewsModalOpen(false);
          setIsReviewModalOpen(open);
        }}
        categoryUrn={selectedCategoryUrn}
      />

      {!hourlyOrderLoading ? (
        <BookHourlyCoachingModal
          categoryUrn={selectedCategoryUrn}
          coach={coach}
          services={services}
          defaultValues={{
            coachUrn: coach.id,
            hourlyOrder: {
              timeInMinute:
                hourlyOrderData?.order?.hourlyOrder?.timeInMinute ??
                mostRecentHourlyOrder?.timeInMinute ??
                DEFAULT_HOURLY_ORDER_MINUTES,
            },
            numberOfCharges: 1,
            categoryUrn: selectedCategoryUrn,
          }}
          orderUrn={hourlyOrderUrn}
        />
      ) : null}
      <LoginModal onApplicantLogin={() => setLoginModalOpen(false)} />
    </>
  );
};

export default CoachPage;
